import { AnimatePresence, motion } from 'motion/react';
import { useId, useState } from 'react';
import { useIntlayer } from 'react-intlayer';

import { VacancyDescription } from './VacancyDescription';
import { VacancyFilterCombobox } from './VacancyFilterCombobox';

import { Button } from '~/components/ui/button';
import { FadeIn } from '~/components/ui/fade-in';
import { ArrowUpRight } from '~/components/ui/icons';
import type {
  PeopleForceVacanciesPayload,
  PeopleForceVacancy,
} from '~/lib/peopleforce';
import { cn } from '~/lib/utils';

const toggleSelectedValue = (selectedValues: string[], value: string) => {
  return selectedValues.includes(value)
    ? selectedValues.filter((selectedValue) => selectedValue !== value)
    : [...selectedValues, value];
};

const getUniqueOptions = (values: Array<string | null>) => {
  return [
    ...new Set(values.filter((value): value is string => Boolean(value))),
  ].sort((left, right) => left.localeCompare(right));
};

const getVacancyMeta = (vacancy: PeopleForceVacancy) => {
  return [vacancy.location, vacancy.department, vacancy.employmentType]
    .filter((value): value is string => Boolean(value))
    .join(' / ');
};

const VACANCY_EXPAND_EASE = [0.4, 0, 0.2, 1] as const;
const VACANCY_POSITION_FADE_DURATION = 0.32;

const VacancyStateCard = ({
  title,
  description,
  actionLabel,
  onAction,
}: {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}) => {
  return (
    <FadeIn delay={0.12}>
      <article className="border border-graphite-dark bg-slate-primary px-5 py-8 md:px-6 md:py-10">
        <div className="max-w-[628px]">
          <h2 className="font-manrope text-[24px] font-normal leading-[1.2] text-white md:text-[30px]">
            {title}
          </h2>
          <p className="mt-4 font-manrope text-base leading-[1.5] text-graphite-700">
            {description}
          </p>
          {actionLabel && onAction ? (
            <button
              type="button"
              className="mt-6 cursor-pointer font-fragment-mono text-xs uppercase tracking-[0.12px] text-blue-primary transition-opacity hover:opacity-70"
              onClick={onAction}
            >
              {actionLabel}
            </button>
          ) : null}
        </div>
      </article>
    </FadeIn>
  );
};

const VacancyListItem = ({
  vacancy,
  isExpanded,
  readMoreLabel,
  hideDetailsLabel,
  applyLabel,
  noDescriptionLabel,
  onToggleExpanded,
}: {
  vacancy: PeopleForceVacancy;
  isExpanded: boolean;
  readMoreLabel: string;
  hideDetailsLabel: string;
  applyLabel: string;
  noDescriptionLabel: string;
  onToggleExpanded: () => void;
}) => {
  const meta = getVacancyMeta(vacancy);
  const descriptionId = useId();
  const toggleId = useId();

  return (
    <motion.article
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: VACANCY_POSITION_FADE_DURATION,
        ease: VACANCY_EXPAND_EASE,
      }}
      className="border-b border-graphite-dark"
    >
      <div
        className={cn(
          'group flex flex-col gap-6 px-4 py-8 transition-colors duration-300 md:px-6 md:py-10 lg:flex-row lg:items-center lg:justify-between lg:gap-6',
          !isExpanded && 'hover:bg-blue-primary'
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
          <h2 className="font-manrope text-[20px] leading-[1.4] font-normal text-white md:text-[24px] md:leading-none">
            {vacancy.title}
          </h2>
          {meta ? (
            <p className="truncate font-fragment-mono text-[12px] leading-normal tracking-[0.12px] text-graphite-700 uppercase md:text-[16px] md:tracking-[0.16px]">
              {meta}
            </p>
          ) : null}
        </div>

        <div className="flex w-full items-start gap-2.5 lg:w-auto lg:shrink-0 lg:items-center lg:justify-end lg:gap-4">
          {vacancy.description ? (
            <Button
              type="button"
              variant="text-link"
              className="order-2 h-[42px] w-[120px] shrink-0 justify-center px-0 py-2.5 text-[16px] font-medium tracking-[0.16px] text-white no-underline hover:no-underline lg:order-1 lg:w-[110px]"
              onClick={onToggleExpanded}
              aria-expanded={isExpanded}
              aria-controls={descriptionId}
              id={toggleId}
            >
              {isExpanded ? hideDetailsLabel : readMoreLabel}
            </Button>
          ) : null}

          {vacancy.applyUrl ? (
            <Button
              variant="glass"
              className={cn(
                'order-1 h-[42px] flex-1 justify-center border-0 bg-white/8 px-6 py-2.5 text-[16px] font-medium tracking-[0.16px] text-white normal-case lg:order-2 lg:flex-none',
                !isExpanded &&
                  'hover:border-0 hover:bg-white/8 group-hover:bg-white/8'
              )}
              asChild
            >
              <a href={vacancy.applyUrl} target="_blank" rel="noreferrer">
                {applyLabel}
                <ArrowUpRight className="size-[14px]" />
              </a>
            </Button>
          ) : null}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded ? (
          <motion.div
            id={descriptionId}
            role="region"
            aria-labelledby={toggleId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.35, ease: VACANCY_EXPAND_EASE },
              opacity: { duration: 0.2, ease: VACANCY_EXPAND_EASE },
            }}
            className="overflow-hidden"
          >
            <VacancyDescription
              description={vacancy.description}
              noDescriptionLabel={noDescriptionLabel}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.article>
  );
};

interface VacanciesPageContentProps {
  vacanciesData: PeopleForceVacanciesPayload;
}

export const VacanciesPageContent = ({
  vacanciesData,
}: VacanciesPageContentProps) => {
  const { hero, list } = useIntlayer('vacancies');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState<
    string[]
  >([]);
  const [expandedVacancyId, setExpandedVacancyId] = useState<string | null>(
    null
  );

  const hasVacancies =
    vacanciesData.status === 'ready' && vacanciesData.vacancies.length > 0;
  const isVacanciesUnavailable =
    vacanciesData.status === 'error' || vacanciesData.status === 'unconfigured';
  const locationOptions = getUniqueOptions(
    vacanciesData.vacancies.map((vacancy) => vacancy.location)
  );
  const departmentOptions = getUniqueOptions(
    vacanciesData.vacancies.map((vacancy) => vacancy.department)
  );
  const employmentTypeOptions = getUniqueOptions(
    vacanciesData.vacancies.map((vacancy) => vacancy.employmentType)
  );
  const hasActiveFilters =
    selectedLocations.length > 0 ||
    selectedDepartments.length > 0 ||
    selectedEmploymentTypes.length > 0;
  const filteredVacancies = vacanciesData.vacancies.filter((vacancy) => {
    const matchesLocation =
      selectedLocations.length === 0 ||
      (vacancy.location !== null &&
        selectedLocations.includes(vacancy.location));
    const matchesDepartment =
      selectedDepartments.length === 0 ||
      (vacancy.department !== null &&
        selectedDepartments.includes(vacancy.department));
    const matchesEmploymentType =
      selectedEmploymentTypes.length === 0 ||
      (vacancy.employmentType !== null &&
        selectedEmploymentTypes.includes(vacancy.employmentType));

    return matchesLocation && matchesDepartment && matchesEmploymentType;
  });

  const clearFilters = () => {
    setSelectedLocations([]);
    setSelectedDepartments([]);
    setSelectedEmploymentTypes([]);
  };

  return (
    <div className="min-h-full w-full bg-black text-white">
      <section className="px-4 pb-24 pt-[132px] md:px-8 md:pb-36 md:pt-[168px] lg:px-20 lg:pb-[240px] lg:pt-[202px]">
        <div className="w-full">
          <h1 className="sr-only">{hero.pageTitle}</h1>

          <FadeIn>
            <p className="font-fragment-mono text-[14px] uppercase leading-[1.5] tracking-[0.14px] text-white">
              {hero.label}{' '}
              <span className="text-blue-primary">
                ({vacanciesData.vacancies.length})
              </span>
            </p>
          </FadeIn>

          <FadeIn>
            <p className="mt-5 max-w-[628px] font-manrope text-[26px] font-normal leading-[1.18] text-white md:text-[30px]">
              {hero.description}
            </p>
          </FadeIn>

          {hasVacancies ? (
            <FadeIn>
              <div className="mt-16 md:mt-20">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="grid w-full gap-4 md:grid-cols-3 md:gap-6 xl:max-w-[958px]">
                    <VacancyFilterCombobox
                      label={list.filters.location}
                      options={locationOptions}
                      selectedValues={selectedLocations}
                      onToggle={(value) =>
                        setSelectedLocations((currentValues) =>
                          toggleSelectedValue(currentValues, value)
                        )
                      }
                    />
                    <VacancyFilterCombobox
                      label={list.filters.department}
                      options={departmentOptions}
                      selectedValues={selectedDepartments}
                      onToggle={(value) =>
                        setSelectedDepartments((currentValues) =>
                          toggleSelectedValue(currentValues, value)
                        )
                      }
                    />
                    <VacancyFilterCombobox
                      label={list.filters.employmentType}
                      options={employmentTypeOptions}
                      selectedValues={selectedEmploymentTypes}
                      onToggle={(value) =>
                        setSelectedEmploymentTypes((currentValues) =>
                          toggleSelectedValue(currentValues, value)
                        )
                      }
                    />
                  </div>

                  {hasActiveFilters ? (
                    <button
                      type="button"
                      className="w-fit cursor-pointer pt-3 font-fragment-mono text-xs uppercase tracking-[0.12px] text-blue-primary transition-opacity hover:opacity-70"
                      onClick={clearFilters}
                    >
                      {list.clearFiltersButton}
                    </button>
                  ) : null}
                </div>
              </div>
            </FadeIn>
          ) : null}

          <div className="mt-10">
            {hasVacancies ? (
              <FadeIn>
                {filteredVacancies.length > 0 ? (
                  <motion.div
                    key="vacancies-list"
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: VACANCY_POSITION_FADE_DURATION,
                      ease: VACANCY_EXPAND_EASE,
                    }}
                    className="border-t border-graphite-dark"
                  >
                    <AnimatePresence>
                      {filteredVacancies.map((vacancy) => (
                        <VacancyListItem
                          key={vacancy.id}
                          vacancy={vacancy}
                          isExpanded={expandedVacancyId === vacancy.id}
                          readMoreLabel={list.readMoreButton}
                          hideDetailsLabel={list.hideDetailsButton}
                          applyLabel={list.applyButton}
                          noDescriptionLabel={list.noDescription}
                          onToggleExpanded={() =>
                            setExpandedVacancyId((currentValue) =>
                              currentValue === vacancy.id ? null : vacancy.id
                            )
                          }
                        />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  <motion.div
                    key="filtered-empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: VACANCY_POSITION_FADE_DURATION,
                      ease: VACANCY_EXPAND_EASE,
                    }}
                  >
                    <VacancyStateCard
                      title={list.filteredEmptyTitle}
                      description={list.filteredEmptyDescription}
                      actionLabel={list.clearFiltersButton}
                      onAction={clearFilters}
                    />
                  </motion.div>
                )}
              </FadeIn>
            ) : isVacanciesUnavailable ? (
              <VacancyStateCard
                title={list.unavailableTitle}
                description={list.unavailableDescription}
              />
            ) : (
              <VacancyStateCard
                title={list.emptyTitle}
                description={list.emptyDescription}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
